import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Page from 'layout/page'
import UserContext from 'context/user-context'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'
import {
  ASK_CONVERSATIONS_KEY,
  askConversationKey,
  useAskConversation,
  useAskConversations,
  useCancelAskTurn,
  useDeleteAskConversation,
  useSendAskMessage
} from 'api/hooks/use-ask'
import AskThread from './ask-thread'
import { isAssistantEnabled } from './access'
import ConversationList from './conversation-list'
import CreatedPanel from './created-panel'

// Matches the server's STALE_RUN_AFTER: a running turn quiet for longer than this lost its worker.
const STALE_MS = 180000
const POLL_MS = 1200

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback

const Ask = () => {
  const user = useContext(UserContext)
  const aiEnabled = isAssistantEnabled(user)
  const { id: conversationId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [pendingUserText, setPendingUserText] = useState(null)

  const conversationsQuery = useAskConversations({ options: { enabled: aiEnabled } })
  const conversationQuery = useAskConversation({
    conversationId,
    options: { refetchInterval: (data) => (data?.status === 'running' ? POLL_MS : false) }
  })
  const conversation = conversationQuery.data

  const { mutate: send, isLoading: isSending } = useSendAskMessage()
  const { mutate: cancel, isLoading: isCancelling } = useCancelAskTurn({
    options: {
      onSuccess: () => queryClient.invalidateQueries(askConversationKey(conversationId)),
      onError: () => toast.error('Could not stop the assistant')
    }
  })
  const { mutate: destroy } = useDeleteAskConversation({
    options: {
      onSuccess: (_data, deletedId) => {
        queryClient.invalidateQueries(ASK_CONVERSATIONS_KEY)
        if (deletedId === conversationId) navigate('/ask')
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not delete that conversation'))
    }
  })

  const updatedAtMs = conversation?.updated_at ? new Date(conversation.updated_at).getTime() : null
  const isStale = conversation?.status === 'running' && updatedAtMs != null && Date.now() - updatedAtMs > STALE_MS
  const isRunning = isSending || (conversation?.status === 'running' && !isStale)
  const isStopping = isCancelling || !!conversation?.cancel_requested

  // A chart redrawn in place (update_chart, e.g. its own filters) can show different data under the
  // same query key, so that one chart is refetched. Charts drawn for earlier answers are left alone,
  // and opening or switching conversations sets the baseline without refetching anything.
  const chartDraws = useRef({ conversationId: null, drawn: {} })
  useEffect(() => {
    if (!conversation?.events) return
    const drawn = {}
    conversation.events
      .filter((event) => event.type === 'chart')
      .forEach((event) => { drawn[event.payload.report_chart_id] = event.id })
    const previous = chartDraws.current
    if (previous.conversationId === conversationId) {
      Object.entries(drawn)
        .filter(([chartId, eventId]) => previous.drawn[chartId] && previous.drawn[chartId] !== eventId)
        .forEach(([chartId]) => queryClient.invalidateQueries(['report-chart-data', conversation.report_id, chartId]))
    }
    chartDraws.current = { conversationId, drawn }
  }, [conversation?.events, conversation?.report_id, conversationId, queryClient])

  const handleSend = (message, { onFailure } = {}) => {
    setPendingUserText(message)
    send(
      { message, conversationId },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(askConversationKey(data.id), data)
          queryClient.invalidateQueries(ASK_CONVERSATIONS_KEY)
          if (data.id !== conversationId) navigate(`/ask/${data.id}`)
        },
        onError: (error) => {
          onFailure?.()
          toast.error(errorMessage(error, 'Could not send that question'))
        },
        onSettled: () => setPendingUserText(null)
      }
    )
  }

  const handleDelete = (id) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this conversation? Views it saved stay in your ThirdLine Assistant folder.')) destroy(id)
  }

  if (!aiEnabled) {
    return (
      <Page title="Assistant">
        <Alert variant="info" data-testid="ask-unavailable">
          The Assistant is not turned on for your organization. An administrator can enable AI features in Settings.
        </Alert>
      </Page>
    )
  }

  const renderThread = () => {
    if (conversationId && conversationQuery.isLoading) return <CardLoading minHeight={300} />
    if (conversationId && conversationQuery.isError) {
      return <CardError title="Couldn't load this conversation" onRetry={conversationQuery.refetch} />
    }
    return (
      <AskThread
        conversation={conversationId ? conversation : null}
        isRunning={isRunning}
        isStopping={isStopping}
        isSending={isSending}
        pendingUserText={pendingUserText}
        // Every arrival at a new question focuses the composer, including a click on the /ask already open.
        focusKey={conversationId ? null : location.key}
        onSend={handleSend}
        onStop={() => conversationId && cancel(conversationId)}
      />
    )
  }

  return (
    <Page title="Assistant" pageTitle="ThirdLine Assistant">
      <Row className="g-3">
        <Col lg={9}>
          {/* overflow-hidden lets the body shrink to the card, so the thread scrolls instead of spilling out. */}
          <Card style={{ height: 'calc(100vh - 140px)' }} data-testid="ask-thread-card">
            <Card.Body className="overflow-hidden">{renderThread()}</Card.Body>
          </Card>
        </Col>
        <Col lg={3} className="d-flex flex-column gap-3">
          {conversationId && conversation?.events && (
            <CreatedPanel events={conversation.events} />
          )}
          <Card body>
            <ConversationList
              query={conversationsQuery}
              activeId={conversationId}
              onSelect={(id) => navigate(`/ask/${id}`)}
              onNew={() => navigate('/ask')}
              onDelete={handleDelete}
              disabled={isRunning}
            />
          </Card>
        </Col>
      </Row>
    </Page>
  )
}

export default Ask
